/**
 * Drawing a slide.
 *
 * One look, defined once, shared by every export that has to *paint* a slide
 * rather than hand it to something else: the PNG images and, through the same
 * geometry, the PowerPoint text boxes. The print/PDF path renders HTML instead
 * — the browser is better at that than a canvas is — but reads its sizes from
 * the same constants so the three do not drift apart.
 *
 * Deliberately plain. This app decides *what is on the slide and when*;
 * presentation software decides how it looks. Anyone who wants control beyond
 * this exports PPTX and restyles it there, which is why PPTX ships real text
 * boxes rather than pictures of text.
 *
 * Every entry point takes a `SlideStyle` — the theme and the frame shape —
 * rather than a growing tail of positional arguments, so a caller cannot paint
 * a preview in one aspect ratio and export the same deck in another.
 */

import type { Slide } from "@/lib/slides";
import {
  DEFAULT_SLIDE_STYLE,
  SLIDE_FACES,
  SLIDE_GEOMETRY,
  type SlideStyle,
} from "@/lib/slide-themes";

/** What every drawing entry point takes beyond the slide itself. */
export interface DrawOptions extends Partial<SlideStyle> {
  /** Deck length, for the `3 / 12` in the footer. Zero or one hides it. */
  total?: number;
}

/** Fill in whatever the caller left out. */
function settle(options: DrawOptions): SlideStyle & { total: number } {
  return {
    theme: options.theme ?? DEFAULT_SLIDE_STYLE.theme,
    aspect: options.aspect ?? DEFAULT_SLIDE_STYLE.aspect,
    total: options.total ?? 0,
  };
}

/**
 * Strip inline markdown so a slide never shows its own syntax.
 *
 * The block parser keeps line-level structure but leaves `**bold**` in the
 * text, which is right for the Marp export — that syntax is meaningful there —
 * and wrong everywhere a slide is painted.
 */
export function plainText(text: string): string {
  return (
    text
      .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/(\*\*|__)(.*?)\1/g, "$2")
      .replace(/(\*|_)(.*?)\1/g, "$2")
      .replace(/~~(.*?)~~/g, "$1")
      .replace(/\^\[([^\]]*)\]/g, "")
      // Cutting a citation out of the middle of a sentence leaves the space
      // either side of it behind, and on a slide set at 54px that gap shows.
      .replace(/[ \t]{2,}/g, " ")
      .trim()
  );
}

/**
 * Break `text` into lines no wider than `maxWidth`.
 *
 * Takes a measuring function rather than a canvas context so the wrapping rule
 * can be tested without one — see `slide-output.test.ts`.
 *
 * A word that does not fit on a line of its own is broken mid-word rather than
 * left to run off the edge. That case is rare in prose and routine in a sermon:
 * a URL, a transliterated Greek word, a hyphen-free compound. Overflowing the
 * frame silently is the one outcome a slide must never have, because nothing
 * downstream clips it back into view — the PNG just has words falling off it.
 */
export function wrapText(
  text: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return [];

  /** Split one over-long word into the largest pieces that each fit. */
  const shatter = (word: string): string[] => {
    const pieces: string[] = [];
    let piece = "";
    for (const character of word) {
      if (piece && measure(piece + character) > maxWidth) {
        pieces.push(piece);
        piece = character;
      } else {
        piece += character;
      }
    }
    if (piece) pieces.push(piece);
    return pieces;
  };

  const lines: string[] = [];
  let line = "";

  const place = (word: string) => {
    const candidate = line ? `${line} ${word}` : word;
    if (measure(candidate) <= maxWidth) {
      line = candidate;
      return;
    }
    if (line) lines.push(line);
    // A word too wide even alone: break it, keeping the last piece open so the
    // next word can still join it.
    if (measure(word) > maxWidth) {
      const pieces = shatter(word);
      lines.push(...pieces.slice(0, -1));
      line = pieces[pieces.length - 1] ?? "";
    } else {
      line = word;
    }
  };

  for (const word of words) place(word);
  if (line) lines.push(line);
  return lines;
}

type DrawnLine = {
  text: string;
  size: number;
  font: string;
  weight: string;
  colour: string;
  indent: number;
  /** Distance to the next line's baseline. Carries the gap after a block. */
  advance: number;
};

/**
 * Turn a slide's blocks into positioned lines.
 *
 * Split out from the painting so the overflow pass can measure the whole slide
 * and shrink it before anything is drawn — a slide with eight bullets should
 * get smaller text, not text that runs off the bottom edge.
 *
 * Spacing is per *block*, not per line: consecutive bullets sit close together
 * because they are one list, while a heading gets real air beneath it. Spacing
 * them uniformly is what made the first version look like a wall of text.
 */
function composeLines(
  context: CanvasRenderingContext2D,
  slide: Slide,
  scale: number,
  style: SlideStyle,
): DrawnLine[] {
  const { margin, titleSize, titleOnlySize, bodySize, quoteSize, lineGap } = SLIDE_GEOMETRY;
  const { ink, soft } = style.theme;
  const sans = SLIDE_FACES[style.theme.titleFace];
  const serif = SLIDE_FACES[style.theme.quoteFace];
  const contentWidth = style.aspect.width - margin * 2;
  const lines: DrawnLine[] = [];

  const push = (options: {
    text: string;
    size: number;
    font: string;
    weight?: string;
    colour?: string;
    indent?: number;
    /** Marker drawn on the first line only; continuations hang past it. */
    marker?: string;
    gapAfter: number;
  }) => {
    const size = options.size * scale;
    const weight = options.weight ?? "";
    const indent = (options.indent ?? 0) * scale;
    context.font = `${weight} ${size}px ${options.font}`.trim();

    const markerWidth = options.marker ? context.measureText(`${options.marker}  `).width : 0;
    const wrapped = wrapText(options.text, contentWidth - indent - markerWidth, (text) =>
      context.measureText(text).width,
    );
    if (!wrapped.length) return;

    wrapped.forEach((text, index) => {
      const last = index === wrapped.length - 1;
      lines.push({
        text: index === 0 && options.marker ? `${options.marker}  ${text}` : text,
        size,
        font: options.font,
        weight,
        colour: options.colour ?? ink,
        // Continuations hang past the bullet rather than tucking under it.
        indent: indent + (index === 0 ? 0 : markerWidth),
        advance: size * lineGap + (last ? options.gapAfter * scale : 0),
      });
    });
  };

  for (const block of slide.blocks) {
    switch (block.kind) {
      case "heading": {
        const base = slide.layout === "title" ? titleOnlySize : titleSize;
        const size = block.level > 1 ? base * 0.7 : base;
        push({
          text: plainText(block.text),
          size,
          font: sans,
          weight: "600",
          gapAfter: slide.layout === "title" ? bodySize * 0.3 : bodySize * 0.7,
        });
        break;
      }
      case "bullet":
        push({
          text: plainText(block.text),
          size: bodySize,
          font: sans,
          indent: block.depth * 70,
          marker: block.marker,
          gapAfter: bodySize * 0.32,
        });
        break;
      case "quote":
        push({
          text: `“${plainText(block.text)}”`,
          size: quoteSize,
          font: serif,
          gapAfter: bodySize * 0.5,
        });
        break;
      case "image":
        // Images are not painted here — a canvas cannot fetch a cross-origin
        // picture without tainting itself, which would break toBlob entirely.
        // The alt text stands in; the PDF export shows the real image, since
        // there the browser loads it the ordinary way.
        push({
          text: plainText(block.alt) || "(image)",
          size: bodySize * 0.8,
          font: sans,
          colour: soft,
          gapAfter: bodySize * 0.5,
        });
        break;
      default:
        push({
          text: plainText(block.text),
          size: bodySize,
          font: sans,
          gapAfter: bodySize * 0.5,
        });
    }
  }

  // Trailing air below the last block is the margin's job, not the block's.
  const last = lines[lines.length - 1];
  if (last) last.advance = last.size * lineGap;

  return lines;
}

function totalHeight(lines: DrawnLine[]): number {
  return lines.reduce((sum, line) => sum + line.advance, 0);
}

/**
 * How small the type is allowed to get before a slide is simply overfull.
 *
 * Shared with the print sheet, which runs the same shrink in the browser, so
 * one slide cannot come out two sizes in two exports.
 */
export const MIN_SLIDE_SCALE = 0.45;

/**
 * Paint one slide onto a 2D context sized to `style.aspect`.
 *
 * Shrinks the type until the content fits rather than clipping it. A slide that
 * needs 60% type is a slide with too much on it, but showing all of it small
 * beats showing three-quarters of it large.
 */
export function drawSlide(
  context: CanvasRenderingContext2D,
  slide: Slide,
  options: DrawOptions = {},
): void {
  const style = settle(options);
  const { theme, aspect, total } = style;
  const { background, accent, soft, ornament } = theme;
  const { margin } = SLIDE_GEOMETRY;
  const sans = SLIDE_FACES[theme.titleFace];

  context.fillStyle = background;
  context.fillRect(0, 0, aspect.width, aspect.height);

  // Leave the footer strip out of the text area so long slides never collide
  // with the rule and the slide number. A theme with no ornament has nothing
  // down there, so it gets the space back.
  const footer = ornament ? SLIDE_GEOMETRY.footer : 0;
  const available = aspect.height - margin * 2 - footer;

  let scale = 1;
  let lines = composeLines(context, slide, scale, style);
  while (totalHeight(lines) > available && scale > MIN_SLIDE_SCALE) {
    scale -= 0.05;
    lines = composeLines(context, slide, scale, style);
  }

  const height = totalHeight(lines);
  // Title and quote cards centre; a content slide hangs from the top so a deck
  // of them does not appear to jump about as bullet counts change.
  const centred = slide.layout !== "content";
  // Clamped at the top margin: a centred card that is still overfull at the
  // smallest type would otherwise start above the frame and lose its first
  // line entirely, which is worse than running past the bottom.
  let y = centred ? Math.max(margin, (aspect.height - footer - height) / 2) : margin;

  context.textBaseline = "top";
  for (const line of lines) {
    context.font = `${line.weight} ${line.size}px ${line.font}`.trim();
    context.fillStyle = line.colour;
    context.textAlign = centred ? "center" : "left";
    context.fillText(line.text, centred ? aspect.width / 2 : margin + line.indent, y);
    y += line.advance;
  }

  // A short accent rule so an exported deck reads as one set, and the slide
  // number opposite it — the thing an AV desk asks for by name mid-service.
  // Both are ornament: a theme that wants nothing but words draws neither.
  if (!ornament) return;

  const baseline = aspect.height - margin + 20;
  context.fillStyle = accent;
  context.fillRect(margin, baseline, 120, 9);

  if (total > 1) {
    context.font = `32px ${sans}`;
    context.fillStyle = soft;
    context.textAlign = "right";
    context.textBaseline = "middle";
    context.fillText(`${slide.index + 1} / ${total}`, aspect.width - margin, baseline + 4);
  }
}

/**
 * Draw a slide into a canvas at a fraction of full size.
 *
 * `drawSlide` works in a fixed frame, so a thumbnail is the same call under a
 * transform rather than a second set of measurements. That is the point: the
 * preview in the editor is painted by the code that writes the PNGs, so it
 * cannot quietly disagree with what actually exports.
 */
export function drawSlideScaled(
  canvas: HTMLCanvasElement,
  slide: Slide,
  ratio: number,
  options: DrawOptions = {},
): void {
  const style = settle(options);
  canvas.width = Math.round(style.aspect.width * ratio);
  canvas.height = Math.round(style.aspect.height * ratio);

  const context = canvas.getContext("2d");
  if (!context) return;

  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  drawSlide(context, slide, style);
}

/** Render a slide to a PNG blob at full resolution. */
export async function slideToPng(slide: Slide, options: DrawOptions = {}): Promise<Blob> {
  const style = settle(options);
  const canvas = document.createElement("canvas");
  canvas.width = style.aspect.width;
  canvas.height = style.aspect.height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not get a 2D canvas context for the slide.");

  drawSlide(context, slide, style);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas produced no image data."))),
      "image/png",
    );
  });
}
